"use client";

import {
	ActionButton,
	type ActionButtonProps,
} from "@/components/ActionButton";
import { Container } from "@/components/Container";
import {
	AdaptiveText,
	type AdaptiveTextProps,
	Title,
} from "@/components/Content";
import { Sizes } from "@/components/Content/styles";
import { GradientBackground } from "@/components/Gradient";
import {
	ScaleIn,
	StaggerContainer,
	StaggerItem,
} from "@/components/animations/page-transitions";
import { cn } from "@/lib/utils";
import type { ImageDetails } from "@/types";
import Image from "next/image";

type Props = {
	id?: string;
	title: string;
	text: AdaptiveTextProps["portableText"];
	subtitle: string;
	subtitleText: AdaptiveTextProps["portableText"];
	image: ImageDetails;
	action?: ActionButtonProps;
	className?: string;
};

const CareersHero = ({
	id,
	title,
	text,
	subtitle,
	subtitleText,
	image,
	action,
	className,
}: Props) => {
	return (
		<section id={id} className="relative overflow-hidden pt-40 pb-32 xl:pt-64">
			<GradientBackground />
			<Container className={cn("relative", className)}>
				<StaggerContainer>
					<StaggerItem>
						<Title as="h1" size={Sizes.SecondaryHero} className="max-w-4xl">
							{title}
						</Title>
					</StaggerItem>
					<StaggerItem>
						<AdaptiveText
							portableText={text}
							size={Sizes.SecondaryHero}
							className="max-w-3xl"
						/>
					</StaggerItem>
					<div className="flex flex-col gap-4 xl:flex-row">
						<div>
							<StaggerItem>
								<Title
									as="h2"
									className="mt-16 max-w-3xl text-xl sm:text-3xl md:text-4xl xl:max-w-xl"
								>
									{subtitle}
								</Title>
							</StaggerItem>
							<StaggerItem>
								<AdaptiveText
									portableText={subtitleText}
									className="mt-2 max-w-2xl text-base/6 sm:text-lg/7 xl:max-w-md"
								/>
							</StaggerItem>

							{action && (
								<StaggerItem>
									<ActionButton {...action} className="mt-6" />
								</StaggerItem>
							)}
						</div>
						<StaggerItem>
							<div className="relative mt-16 p-2 md:m-4 md:mt-16 xl:max-w-2xl">
								<div className="-inset-x-12 absolute top-0 h-px bg-neutral-200" />
								<div className="-inset-x-12 absolute bottom-0 h-px bg-neutral-200" />
								<div className="-inset-y-12 absolute left-0 w-px bg-neutral-200" />
								<div className="-inset-y-12 absolute right-0 w-px bg-neutral-200" />
								<ScaleIn>
									<Image
										src={image.src}
										alt={image.alt}
										width={image.width || 1920}
										height={image.height || 1080}
										className="h-full w-full rounded-md border border-neutral-100 object-cover"
									/>
								</ScaleIn>
							</div>
						</StaggerItem>
					</div>
				</StaggerContainer>
			</Container>
		</section>
	);
};

export default CareersHero;
